// BHP-1044: central React Query client for server-state.
// Server data (customers, domains, invoices, quotes, vps, ...) moves off Redux
// thunks onto React Query; see docs/architecture.md.
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '../http/ApiError';

/**
 * BHP-1249: a 403 from the API means "not allowed", and an immediate second
 * try gets the same answer; it only delays the message. Anything else gets
 * one more try, a 401 included: the HttpClient reads the token from a cookie
 * that AuthCheckProvider writes after the first queries have started (and
 * again after each renewal), so a 401 can be a token that was a moment late.
 * The admin copy also skips 401, because its session layer renews before a
 * request leaves; the portal does not have that yet.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const forbidden =
    error instanceof ApiError && error.fromBackend && error.status === 403;
  return !forbidden && failureCount < 1;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Sensible defaults for the customer portal: cache briefly, don't
        // refetch aggressively; the retry rule is shouldRetry's.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        refetchOnWindowFocus: false
      }
    }
  });
}
